import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { plural } from '@biuro/shared';
import { api } from '../api.ts';
import { FileDrop, acceptsFile, draggingFiles } from '../FileDrop.tsx';
import { useMe } from '../hooks.tsx';

type Result = { name: string; kind: 'created' | 'duplicate' | 'error'; text: string };

/** Podsumowanie partii, np. „Dodano 1 szkic, 1 pominięty (już jest), 1 z błędem.” */
export function importSummary(results: Result[]): string {
  const n = (k: Result['kind']) => results.filter((r) => r.kind === k).length;
  const created = n('created'), dup = n('duplicate'), err = n('error');
  const rest = [
    dup ? `${dup} ${plural(dup, 'pominięty', 'pominięte', 'pominiętych')} (już jest)` : '',
    err ? `${err} z błędem` : '',
  ].filter(Boolean);
  if (!created) return `Nie dodano nowych faktur: ${rest.join(', ')}.`;
  return `Dodano ${created} ${plural(created, 'szkic', 'szkice', 'szkiców')}${rest.length ? `, ${rest.join(', ')}` : ''}.`;
}

/**
 * Dodawanie faktur z plików na liście: strefa nad listą i nakładka przy przeciąganiu nad listą.
 * Pliki idą na serwer po kolei; każdy staje się szkiciem „do uzupełnienia”.
 */
export function InvoiceDrop({ children }: { children: ReactNode }) {
  const me = useMe();
  const qc = useQueryClient();
  const drive = !!me.data?.google.drive;
  const [progress, setProgress] = useState('');
  const [results, setResults] = useState<Result[] | null>(null);
  const [over, setOver] = useState(false);
  const running = useRef(false);
  const depth = useRef(0);

  const run = async (files: File[]) => {
    if (running.current || !drive || !files.length) return;
    running.current = true;
    setResults(null);
    const out: Result[] = [];
    try {
      for (const [i, f] of files.entries()) {
        setProgress(`Dodaję ${i + 1} z ${files.length}…`);
        try {
          const r = await api.importInvoice(f);
          out.push(r.status === 'created'
            ? { name: f.name, kind: 'created', text: r.message ? `dodany jako szkic – ${r.message}` : 'dodany jako szkic' }
            : { name: f.name, kind: 'duplicate', text: r.message });
        } catch (e) {
          out.push({ name: f.name, kind: 'error', text: (e as Error).message });
        }
      }
    } finally {
      running.current = false;
      setProgress('');
      setResults(out);
      void qc.invalidateQueries({ queryKey: ['data'] });
    }
  };

  const enter = (e: DragEvent) => {
    if (!draggingFiles(e)) return;
    depth.current++;
    setOver(true);
  };
  const leave = (e: DragEvent) => {
    if (!draggingFiles(e)) return;
    depth.current = Math.max(0, depth.current - 1);
    if (!depth.current) setOver(false);
  };
  const dropped = (e: DragEvent) => {
    if (!draggingFiles(e)) return;
    depth.current = 0;
    setOver(false);
    if (e.defaultPrevented) return; // przyjęła je już strefa nad listą
    e.preventDefault();
    void run(Array.from(e.dataTransfer.files || []).filter((f) => acceptsFile(f, 'application/pdf,image/*')));
  };

  return (
    <div className="invdrop" onDragEnter={enter} onDragLeave={leave} onDrop={dropped}
      onDragOver={(e) => { if (draggingFiles(e)) e.preventDefault(); }}>
      <div className="invdrop-zone">
        <FileDrop id="inv-files" multiple label="Przeciągnij PDF-y faktur albo wybierz pliki" disabled={!drive} busy={progress} onFiles={(f) => void run(f)} />
        {!drive && <div className="hint">Połącz Dysk Google w zakładce Firma, żeby dodawać faktury z plików.</div>}
        {results && (
          <div className="note hi drop-results">
            <div className="drop-results-head"><b>{importSummary(results)}</b><button type="button" className="btn sm ghost" onClick={() => setResults(null)}>Zamknij</button></div>
            <ul aria-label="Wyniki dodawania">
              {results.map((r, i) => <li key={i} className={r.kind}><b>{r.name}</b> – {r.text}</li>)}
            </ul>
          </div>
        )}
      </div>
      {children}
      {over && drive && !progress && <div className="drop-overlay" aria-hidden="true"><span>Upuść, żeby dodać faktury</span></div>}
    </div>
  );
}
